import React, { useState, useContext, useMemo, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import FormField from "../../components/form-components/FormField";
import t from "../../lib/translate";
import { Headline } from "../../components/Headline";

import FormService from "../../lib/service/formService";
import { FormSent } from "../FormSent/FormSent";
import FormLayoutContext from "../../layouts/StandardFormLayout/context";
import { resolveSessionValues } from "../resolveSessionValues";

import * as styles from "./sensorikForm.module.less";
import FocusError from "../FocusError";

const isBrowser = typeof window !== "undefined";

function SensorikForm({ formId, locale, locationState = {}, isSending }) {
    const [isSent, setIsSent] = useState(false);
    const [detectors, setDetectors] = useState([]);
    const { setShowSubmit } = useContext(FormLayoutContext);
    const service = new FormService(
        process.env.GATSBY_FORM_MICROSERVICE_BASE_URL,
        locale
    );

    const activeFilterOptions = useMemo(() => {
        if (!isBrowser) return {};

        return JSON.parse(
            window.localStorage.getItem("df_active_options") || "{}"
        );
    }, []);

    function decodeBase64ToObject(base64String) {
        // Decode the Base64 string to a JSON string
        const jsonString = decodeURIComponent(atob(base64String));

        // Parse the JSON string back into an object
        const obj = JSON.parse(jsonString);

        return obj;
    }
    useEffect(() => {
        if (isBrowser) {
            const url = window.location.search;
            try {
                const detectors = decodeBase64ToObject(url.split("?query=")[1]);
                setDetectors(detectors.detectors);
            } catch (e) {
                console.error("Error parsing query param:", e);
            }
        }
    }, []);

    const submit = (userData) => {
        const formData = {
            ...userData,
            language: locale,
            url: locationState?.prevUrl ?? "",
            gdpr: userData.gdpr ? "1" : "0",
            newsletter: userData.newsletter ? "1" : "0",
            contactMe: userData.contactMe ? "1" : "0",
            catalogViaPost: userData.catalogViaPost ? "1" : "0",
            catalogViaEmail: userData.catalogViaEmail ? "1" : "0",
            detectors: detectors ? detectors.map((d) => d.name).join(",") : ""
        };

        const { campaign, pnr, dnr } = resolveSessionValues();

        if (campaign) {
            formData.campaign = campaign.name;
        }

        if (pnr) {
            formData.pnr = pnr;
        }

        if (dnr) {
            formData.dnr = dnr;
        }

        const activeFilterOptions =
            window.localStorage.getItem("df_active_options");

        if (!!activeFilterOptions) {
            formData.filter = activeFilterOptions
                .replace(/[{}"]/g, "")
                .replaceAll(",", ", ")
                .replaceAll(":", ": ");
        }
        isSending(true);
        service
            .submit("sensor-form", formData)
            .then((res) => {
                if (res === null) {
                    window?.dataLayer?.push({
                        event: "form_successfully_submitted"
                    });
                    setIsSent(true);
                    setShowSubmit(false);
                    isSending(false);
                }
            })
            .catch((error) => {
                console.error(error);
                isSending(false);
            });
    };

    return (
        <>
            {!isSent && (
                <Formik
                    initialValues={{
                        email: "",
                        salutation: "",
                        title: "",
                        firstname: "",
                        lastname: "",
                        company: "",
                        country: "",
                        zip: "",
                        city: "",
                        street: "",
                        number: "",
                        phone: "",
                        message: "",
                        catalogViaPost: "",
                        catalogViaEmail: "",
                        contactMe: "",
                        newsletter: "",
                        gdpr: ""
                    }}
                    validationSchema={Yup.object({
                        email: Yup.string()
                            .email()
                            .required(t("form.sensor.email.error")),
                        salutation: Yup.string()
                            .required(t("form.sensor.salutation.error"))
                            .oneOf([
                                // t("form.sensor.salutation.value-01"),
                                t("form.sensor.salutation.value-02"),
                                t("form.sensor.salutation.value-03"),
                                ...(locale === "zh-CN"
                                    ? []
                                    : [t("form.sensor.salutation.value-04")])
                            ]),
                        title: Yup.string(),
                        firstname: Yup.string().required(
                            t("form.sensor.firstname.error")
                        ),
                        lastname: Yup.string().required(
                            t("form.sensor.surname.error")
                        ),
                        company: Yup.string().required(
                            t("form.sensor.company.error")
                        ),
                        country: Yup.string(),
                        zip: Yup.string().required(
                            t("form.sensor.zipcode.error")
                        ),
                        city: Yup.string().required(
                            t("form.sensor.city.error")
                        ),
                        street: Yup.string(),
                        number: Yup.string(),
                        phone: Yup.number().required(
                            t("form.sensor.phone.error")
                        ),
                        message: Yup.string(),
                        catalogViaPost: Yup.boolean(),
                        catalogViaEmail: Yup.boolean(),
                        contactMe: Yup.boolean(),
                        newsletter: Yup.boolean(),
                        gdpr: Yup.boolean().required(
                            t("form.sensor.privacypolicy.error")
                        )
                    })}
                    onSubmit={submit}
                >
                    <Form id={formId}>
                        <legend>
                            {/* <Tagline> {t("form.sensor.tagline")}</Tagline> TODO: Obsolet? */}
                            <Headline semanticOfHeadline="H1">
                                {t("form.sensor.headline")}
                            </Headline>
                            <div>{t("form.sensor.subheadline")}</div>
                        </legend>
                        <div className={styles.selectedFilterBlock}>
                            {Object.keys(activeFilterOptions)
                                .sort()
                                .map((key) => {
                                    let value = activeFilterOptions[key];

                                    if (value === true) {
                                        value = t("detector.filter.choice.yes");
                                    }

                                    if (value === false) {
                                        value = t("detector.filter.choice.no");
                                    }

                                    if (Array.isArray(value)) {
                                        value = value.join(", ");
                                    }

                                    return (
                                        <div className={styles.dataRow}>
                                            <span>
                                                {t(
                                                    `detector.filter.property.${key}`
                                                )}
                                            </span>
                                            <span>{value}</span>
                                        </div>
                                    );
                                })}
                            {detectors && detectors.length > 0 && (
                                <div className={styles.dataRow}>
                                    <span>
                                        {t("detector.filter.choice.detector")}
                                    </span>
                                    <span>
                                        {detectors
                                            .map((detector) => detector.name)
                                            .join(", ")}
                                    </span>
                                </div>
                            )}
                        </div>
                        <FormField
                            type="text"
                            name="email"
                            label={t("form.sensor.email.label")}
                            placeholder={t("form.sensor.email.label")}
                            required
                        />
                        <FormField
                            type="select"
                            name="salutation"
                            label={t("form.sensor.salutation.label")}
                            placeholder={t("form.sensor.salutation.label")}
                            options={[
                                t("form.sensor.salutation.value-01"),
                                t("form.sensor.salutation.value-02"),
                                t("form.sensor.salutation.value-03"),
                                ...(locale === "zh-CN"
                                    ? []
                                    : [t("form.sensor.salutation.value-04")])
                            ]}
                            required
                        />
                        <FormField
                            type="text"
                            name="title"
                            label={t("form.sensor.title.label")}
                            placeholder={t("form.sensor.title.label")}
                        />
                        <FormField
                            type="text"
                            name="firstname"
                            label={t("form.sensor.firstname.label")}
                            placeholder={t("form.sensor.firstname.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="lastname"
                            label={t("form.sensor.surname.label")}
                            placeholder={t("form.sensor.surname.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="company"
                            label={t("form.sensor.company.label")}
                            placeholder={t("form.sensor.company.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="country"
                            label={t("form.sensor.country.label")}
                            placeholder={t("form.sensor.country.label")}
                        />
                        <FormField
                            type="text"
                            name="zip"
                            label={t("form.sensor.zipcode.label")}
                            placeholder={t("form.sensor.zipcode.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="city"
                            label={t("form.sensor.city.label")}
                            placeholder={t("form.sensor.city.label")}
                            required
                        />
                        <FormField
                            type="text"
                            name="street"
                            label={t("form.sensor.street.label")}
                            placeholder={t("form.sensor.street.label")}
                        />
                        <FormField
                            type="text"
                            name="number"
                            label={t("form.sensor.housenumber.label")}
                            placeholder={t("form.sensor.housenumber.label")}
                        />
                        <FormField
                            type="text"
                            name="phone"
                            label={t("form.sensor.phone.label")}
                            placeholder={t("form.sensor.phone.label")}
                            required
                        />
                        <FormField
                            type="textarea"
                            name="message"
                            label={t("form.sensor.yourmessage.label")}
                            placeholder={t("form.sensor.yourmessage.label")}
                        />
                        <FormField
                            type="checkbox"
                            name="catalogViaPost"
                            label={t("form.sensor.catalogbypost.checkbox")}
                            placeholder={t(
                                "form.sensor.catalogbypost.checkbox"
                            )}
                        />
                        <FormField
                            type="checkbox"
                            name="catalogViaEmail"
                            label={t("form.sensor.catalogpdf.checkbox")}
                            placeholder={t("form.sensor.catalogpdf.checkbox")}
                        />
                        <FormField
                            type="checkbox"
                            name="contactMe"
                            label={t("form.sensor.contact.checkbox")}
                            placeholder={t("form.sensor.contact.checkbox")}
                        />{" "}
                        <FormField
                            type="checkbox"
                            name="newsletter"
                            label={t("form.sensor.newsletter.checkbox")}
                            placeholder={t("form.sensor.newsletter.checkbox")}
                        />
                        <FormField
                            type="checkbox"
                            name="gdpr"
                            label={t("form.sensor.privacypolicy.checkbox")}
                            placeholder={t(
                                "form.sensor.privacypolicy.checkbox"
                            )}
                            required
                        />
                        <div>{t("form.sensor.subtitle")}</div>
                        <FocusError />
                    </Form>
                </Formik>
            )}
            {isSent && <FormSent />}
        </>
    );
}

export { SensorikForm };
